import { pb, messageOf, type SharedCharacterResponse } from '@/shared/api'
import type { Character } from '../types/character'
import type { SavedCharacterMeta } from '../model/savedCharactersStore'
import { migrateCharacterKeys, migrateMetaKeys } from './migrate'

// Unambiguous, URL-safe alphabet (no 0/O, 1/l/I) so a share id stays short and
// compact (YouTube-style) while still being readable and typeable by hand.
const ALPHABET = '23456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ'
const ID_LENGTH = 11

/** A random share id, e.g. `dQw4w9WgXcQ`. ~57^11 ≈ 3e19 possibilities. */
export function newShareId(): string {
  // Reject bytes in the final, short modulo bucket so every character is equally
  // likely (no modulo bias).
  const cutoff = 256 - (256 % ALPHABET.length)
  let id = ''
  while (id.length < ID_LENGTH) {
    for (const byte of crypto.getRandomValues(new Uint8Array(ID_LENGTH))) {
      if (byte >= cutoff) continue
      id += ALPHABET[byte % ALPHABET.length]
      if (id.length === ID_LENGTH) break
    }
  }
  return id
}

/** The absolute URL a friend can open to view the shared character. */
export function shareUrl(shareId: string): string {
  return `${window.location.origin}/c/${shareId}`
}

export interface SharedCharacter {
  character: Character
  meta?: SavedCharacterMeta
}

/**
 * Fetch a publicly shared character by its share id, or null if not found.
 *
 * A hook route rather than a collection read, for the same reason this was a
 * SECURITY DEFINER function in Postgres: the caller has no read access to
 * `characters` at all, so there is nothing for them to filter. The route hands
 * back exactly one record's sheet and nothing enumerable.
 */
export async function fetchSharedCharacter(shareId: string): Promise<SharedCharacter | null> {
  try {
    const row = await pb.send<SharedCharacterResponse>(
      `/api/tomf/shared/${encodeURIComponent(shareId)}`,
      { method: 'GET' },
    )
    return {
      // Shared rows are raw JSON written by any past build; normalize legacy keys.
      character: migrateCharacterKeys(row.data as unknown as Character),
      meta: migrateMetaKeys((row.meta as unknown as SavedCharacterMeta | null) ?? undefined),
    }
  } catch (error) {
    // A 404 is the ordinary answer to a revoked or mistyped link, so it is not
    // worth a console entry; anything else is.
    if ((error as { status?: number }).status !== 404) {
      // The reason, not just the fact: a share link that stops working is
      // reported by whoever it was sent to, and "it says nothing" is the worst
      // version of that conversation.
      console.error(`[pb] fetch shared character failed: ${messageOf(error)}`, error)
    }
    return null
  }
}
